import React, { useState, useEffect } from 'react';
import { Sidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  IoHomeOutline,
  IoLogOutOutline,
  IoChevronForwardOutline,
} from 'react-icons/io5';
import { FiUpload, FiUserPlus, FiFileText, FiSettings, FiCalendar, FiPackage, FiDatabase } from 'react-icons/fi';
import { FaBuilding } from 'react-icons/fa';
import '../App.css';

const SidebarMenu = ({ toggled = true, onBackdropClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  useEffect(() => {
    // Set active menu based on current route
    // Menu highlighting is handled by react-pro-sidebar active prop
  }, [location]);

  const handleToggle = () => {
    setCollapsed(!collapsed);
  };

  const isElectron = () => {
    return window && window.process && window.process.type;
  };

  let ipcRenderer;
  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }

  const handleLogout = async () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('clientInfo');
    localStorage.removeItem('webUserInfo');

    if (isElectron()) {
      await ipcRenderer.invoke('store-set', 'clientInfo', {});
    }

    navigate('/login');
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <IoHomeOutline size={19} className="text-gray-700" />,
      path: '/dashboard',
    },
    {
      id: 'subscription',
      label: 'Update Subscriptions',
      icon: <FiCalendar size={19} className="text-gray-700" />,
      path: '/update-subscription',
    },
    {
      id: 'clients',
      label: 'Add Devices',
      icon: <FiUserPlus size={19} className="text-gray-700" />,
      path: '/add-clients',
    },
    {
      id: 'testmaster',
      label: 'Add Test Master',
      icon: <FiFileText size={19} className="text-gray-700" />,
      path: '/add-test-master',
    },
    {
      id: 'groupmaster',
      label: 'Add Group Master',
      icon: <FiFileText size={19} className="text-gray-700" />,
      path: '/add-group-master',
    },
    {
      id: 'departmentmaster',
      label: 'Department Master',
      icon: <FaBuilding size={19} className="text-gray-700" />,
      path: '/department-master',
    },
    {
      id: 'authmodules',
      label: 'Add Auth Modules',
      icon: <FiUpload size={19} className="text-gray-700" />,
      path: '/add-auth-modules',
    },
    {
      id: 'defaultdata',
      label: 'Add Default Data',
      icon: <FiSettings size={19} className="text-gray-700" />,
      path: '/add-default-data',
    },
    {
      id: 'onprempatch',
      label: 'On-Prem Patch',
      icon: <FiPackage size={19} className="text-gray-700" />,
      path: '/create-on-prem-patch',
    },
    {
      id: 'sqlscript',
      label: 'SQL Script Generator',
      icon: <FiDatabase size={19} className="text-gray-700" />,
      path: '/sql-script-generator',
    },
  ];

  const menuItemStyles = {
    button: ({ level, active, disabled }) => {
      return {
        color: disabled ? '#9ca3af' : active ? '#5A57FF' : '#1e293b',
        backgroundColor: active ? '#f0f0ff' : 'transparent',
        fontWeight: active ? '600' : '500',
        fontSize: '14px',
        padding: '12px 16px',
        borderRadius: '8px',
        margin: '4px 8px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: active ? '#f0f0ff' : '#f5f5f5',
          transform: 'translateX(4px)',
        },
      };
    },
    icon: {
      color: '#1e293b',
      fontSize: '19px',
      marginRight: '12px',
    },
  };

  return (
    <div className="h-full">
      <Sidebar
        className="h-full border-r border-gray-200 shadow-sm transition-all duration-300 ease-in-out"
        collapsed={collapsed}
        toggled={toggled}
        onBackdropClick={onBackdropClick}
        backgroundColor="white"
        rootStyles={{
          color: '#1e293b',
          zIndex: 100,
        }}
      >
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-2">
            <div 
              className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center cursor-pointer"
              onClick={handleToggle}
            >
              <span className="text-white font-bold text-lg">F</span>
            </div>
            {!collapsed && (
              <h2 className="text-xl font-bold text-blue-600 transition-opacity duration-300">
                Fluxion Console
              </h2>
            )}
          </div>
        </div>

        <div className="px-3">
          <div className="mb-2 mt-2 h-px bg-gray-200" />
        </div>

        <Menu menuItemStyles={menuItemStyles}>
          {menuItems.map((item) => (
            <MenuItem
              key={item.id}
              icon={item.icon}
              onClick={() => navigate(item.path)}
              active={location.pathname === item.path}
              className="transition-all duration-150 ease-in-out"
              onMouseEnter={() => setHoveredItem(item.id)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <div className="flex items-center justify-between">
                <span>{item.label}</span>
                {hoveredItem === item.id && !collapsed && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-xs text-indigo-600">
                    <IoChevronForwardOutline size={12} />
                  </span>
                )}
              </div>
            </MenuItem>
          ))}

          <div className="px-3 mt-4">
            <div className="mb-2 h-px bg-gray-200" />
          </div>

          <MenuItem
            icon={<IoLogOutOutline size={19} className="text-gray-700" />}
            onClick={handleLogout}
            className="transition-all duration-150 ease-in-out text-red-600"
            onMouseEnter={() => setHoveredItem('logout')}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="flex items-center justify-between">
              <span>Logout</span>
              {hoveredItem === 'logout' && !collapsed && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-xs text-red-600">
                  <IoChevronForwardOutline size={12} />
                </span>
              )}
            </div>
          </MenuItem>
        </Menu>
      </Sidebar>
    </div>
  );
};

export default SidebarMenu;
